<template>
  <div
    v-if="visible"
    class="payment-mark-paid-modal modal fade show"
    style="display: block; background-color: rgba(0, 0, 0, 0.5)"
    tabindex="-1"
  >
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <i class="fas fa-credit-card me-2"></i>
            {{ $t("Mark Payment as Paid") }}
          </h5>
          <button type="button" class="btn-close" @click="closeModal"></button>
        </div>
        <div class="modal-body">
          <div v-if="selectedPayment" class="mb-3">
            <h6>Payment Details</h6>
            <div class="row">
              <div class="col-6">
                <strong>Name:</strong> {{ selectedPayment.name || "N/A" }}
              </div>
              <div class="col-6">
                <strong>Amount:</strong> ${{ formatNumber(selectedPayment.amount) }}
              </div>
            </div>
            <div class="row mt-2">
              <div class="col-6">
                <strong>Contact:</strong>
                {{ getContactName(selectedPayment.contact) }}
              </div>
              <div class="col-6">
                <strong>Current Status:</strong>
                <span
                  :class="
                    selectedPayment.paid ? 'badge bg-success' : 'badge bg-warning'
                  "
                >
                  {{ selectedPayment.paid ? $t("Paid") : $t("Pending") }}
                </span>
              </div>
            </div>
          </div>

          <hr />

          <form @submit.prevent="updatePaymentStatus">
            <div v-if="serverValidationError" class="alert alert-danger mb-3">
              <i class="fas fa-exclamation-triangle me-2"></i>
              {{ serverValidationError }}
            </div>

            <div class="row">
              <div class="col-12 mb-3">
                <label class="form-label"
                  >Payment Date <span class="text-danger">*</span></label
                >
                <input
                  v-model="paymentStatusForm.payment_date"
                  type="date"
                  class="form-control"
                  :class="{
                    'is-invalid': paymentStatusFormErrors.payment_date,
                  }"
                  :placeholder="$t('Select payment date')"
                />
                <div
                  v-if="paymentStatusFormErrors.payment_date"
                  class="invalid-feedback"
                >
                  {{ paymentStatusFormErrors.payment_date }}
                </div>
              </div>

              <div class="col-12 mb-3" v-if="bankAccountField">
                <label class="form-label"
                  >{{ $t(bankAccountField.label || "Bank account (from)") }}
                  <span class="text-danger">*</span></label
                >
                <FieldReference
                  :field="bankAccountField"
                  :value="paymentStatusForm.bankaccount"
                  :referenceOptions="referenceOptions.bankaccount || []"
                  mode="edit"
                  :showLabel="false"
                  @input="(val) => onBankAccountFieldChange(val)"
                />
                <div
                  v-if="paymentStatusFormErrors.bankaccount"
                  class="invalid-feedback"
                >
                  {{ paymentStatusFormErrors.bankaccount }}
                </div>
              </div>

              <div class="col-12 mb-3" v-if="bankAccountToField">
                <label class="form-label">{{
                  $t(bankAccountToField.label || "Bank account (to)")
                }}</label>
                <FieldReference
                  :field="bankAccountToField"
                  :value="paymentStatusForm.bank_account_to"
                  :referenceOptions="referenceOptions.bankaccount || []"
                  mode="edit"
                  :showLabel="false"
                  @input="(val) => onBankAccountToFieldChange(val)"
                />
                <div
                  v-if="paymentStatusFormErrors.bank_account_to"
                  class="invalid-feedback"
                >
                  {{ paymentStatusFormErrors.bank_account_to }}
                </div>
              </div>
            </div>

            <div class="mb-3">
              <h6 class="mb-3">
                <i class="fas fa-link me-2"></i>
                {{ $t("Applied To") }}
              </h6>
              <div
                v-for="(appliedTo, index) in paymentStatusForm.appliedto"
                :key="index"
                class="card mb-3"
              >
                <div class="card-body">
                  <div
                    class="d-flex justify-content-between align-items-center mb-2"
                  >
                    <h6 class="mb-0">{{ $t("Application") }} {{ index + 1 }}</h6>
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-danger"
                      @click="removeAppliedTo(index)"
                    >
                      <i class="fas fa-times"></i>
                    </button>
                  </div>

                  <div class="row">
                    <div class="col-md-6 mb-3">
                      <label class="form-label">{{ $t("Expense") }}</label>
                      <FieldReference
                        :field="{
                          key: 'expense',
                          ref: 'shipping_expenses',
                          label: 'Expense',
                        }"
                        :value="appliedTo.expense"
                        :referenceOptions="referenceOptions.expense || []"
                        mode="edit"
                        :showLabel="false"
                        @input="(val) => onExpenseChange(index, val)"
                      />
                    </div>

                    <div class="col-md-3 mb-3">
                      <label class="form-label">{{ $t("Amount") }}</label>
                      <input
                        v-model.number="appliedTo.amount"
                        type="number"
                        step="0.01"
                        min="0"
                        class="form-control"
                        :placeholder="$t('Amount')"
                      />
                    </div>

                    <div class="col-md-3 mb-3">
                      <label class="form-label">{{ $t("Bank Commission") }}</label>
                      <input
                        v-model.number="appliedTo.bank_commission"
                        type="number"
                        step="0.01"
                        min="0"
                        class="form-control"
                        :placeholder="$t('Bank Commission')"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <button
                type="button"
                class="btn btn-outline-primary btn-sm"
                @click="addAppliedTo"
              >
                <i class="fas fa-plus me-2"></i>
                {{ $t("Add Application") }}
              </button>

              <div class="mt-3">
                <div class="d-flex justify-content-between align-items-center">
                  <strong>{{ $t("Total Applied") }}:</strong>
                  <span :class="getTotalAppliedClass()">
                    ${{ formatNumber(getTotalAppliedAmount()) }}
                  </span>
                </div>
                <div
                  class="d-flex justify-content-between align-items-center mt-1"
                >
                  <strong>{{ $t("Payment Amount") }}:</strong>
                  <span>${{ formatNumber(selectedPayment?.amount || 0) }}</span>
                </div>
                <div
                  v-if="hasAppliedToError()"
                  class="alert alert-danger mt-2 mb-0"
                >
                  <i class="fas fa-exclamation-triangle me-2"></i>
                  {{
                    $t(
                      "Total applied amount cannot exceed payment amount"
                    )
                  }}
                </div>
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label">Comment</label>
              <textarea
                v-model="paymentStatusForm.comment"
                class="form-control"
                rows="3"
                placeholder="Optional comment about the payment..."
              ></textarea>
            </div>

            <div class="mb-3">
              <label class="form-label">Payment Receipt/Proof</label>
              <div class="input-group">
                <input
                  ref="fileInput"
                  type="file"
                  class="form-control"
                  accept="image/*,.pdf,.doc,.docx"
                  @change="handleFileUpload"
                  :disabled="uploadingFile"
                />
                <button
                  v-if="paymentStatusRequestFiles.length"
                  type="button"
                  class="btn btn-outline-danger"
                  @click="removeFile"
                  :disabled="uploadingFile"
                >
                  <i class="fas fa-times"></i>
                </button>
              </div>
              <div v-if="uploadingFile" class="mt-2">
                <div class="progress">
                  <div
                    class="progress-bar"
                    role="progressbar"
                    :style="{ width: uploadProgress + '%' }"
                  >
                    {{ uploadProgress }}%
                  </div>
                </div>
              </div>
              <div
                v-for="(reqFile, reqIdx) in paymentStatusRequestFiles"
                :key="reqIdx"
                class="mt-2"
              >
                <div class="border border-primary d-flex align-items-center mb-2">
                  <i class="fas fa-file me-2"></i>
                  <span>{{ getFileName(reqFile) }}</span>
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-success ms-auto"
                    @click="viewFile(reqFile)"
                  >
                    <i class="fas fa-eye"></i> View
                  </button>
                </div>
              </div>
              <small class="form-text text-muted">
                Supported formats: Images (JPG, PNG, GIF), PDF, Word documents.
                Max size: 10MB
              </small>
            </div>

            <div class="mb-3">
              <div class="form-check">
                <input
                  v-model="paymentStatusForm.paid"
                  class="form-check-input"
                  type="checkbox"
                  id="markAsPaidModal"
                  @change="onPaidStatusChange($event.target.checked)"
                />
                <label class="form-check-label" for="markAsPaidModal">
                  {{ $t("Mark this payment as paid") }}
                  <small v-if="!canMarkAsPaid" class="d-block text-muted">
                    {{
                      $t(
                        "Will be automatically marked when you select a payment date and bank account"
                      )
                    }}
                  </small>
                </label>
              </div>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeModal">
            {{ $t("Cancel") }}
          </button>
          <button
            type="button"
            class="btn btn-success"
            @click="updatePaymentStatus"
            :disabled="updatingPaymentStatus"
          >
            <span
              v-if="updatingPaymentStatus"
              class="spinner-border spinner-border-sm me-2"
              role="status"
            ></span>
            <i v-else class="fas fa-check me-2"></i>
            {{
              updatingPaymentStatus ? $t("Updating...") : $t("Update Payment")
            }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { wencesApi } from "@/api/wencesApi";
import FieldReference from "@/components/gotrading/model-definition-core/FieldReference.vue";
import Swal from "sweetalert2";
import { formatNumberSync } from "@/utils/numberFormatter";
import { exceedsMoneyLimit } from "@/utils/moneyCompare";
import {
  openFileupload,
  showFileuploadAccessError,
  getFileuploadDisplayName,
} from "@/utils/fileuploadAccess";
import {
  appliedToRowsEqual,
  buildPaymentStatusUpdatePayload,
  isPaymentUpdateTransportError,
  paymentRefId,
  verifyPaymentStatusPersisted,
} from "@/utils/paymentMarkPaidUpdate";
import { loadPaymentAppliedToRows } from "@/utils/paymentAppliedToLoad";
import {
  applyMarkPaidExpenseLabels,
  extractDynamicListItems,
  resolveMarkPaidExpenseOption,
  typeofpaymentOptionsFromLists,
} from "@/utils/markPaidExpenseLabel";
import financialReceiptFilesMixin from "@/mixins/financialReceiptFilesMixin";

export default {
  name: "PaymentMarkPaidModal",
  components: { FieldReference },
  mixins: [financialReceiptFilesMixin],
  data() {
    return {
      visible: false,
      selectedPayment: null,
      updatingPaymentStatus: false,
      paymentStatusForm: {
        payment_date: "",
        bankaccount: "",
        bank_account_to: "",
        comment: "",
        paid: true,
        payment_request_file: [],
        appliedto: [],
      },
      originalPaymentStatusForm: null,
      appliedToLoadIncomplete: false,
      paymentStatusFormErrors: {},
      serverValidationError: "",
      uploadingFile: false,
      uploadProgress: 0,
      paymentFields: [],
      referenceOptions: {},
      expenseTypeOptions: [],
      bankAccountField: null,
      bankAccountToField: null,
    };
  },
  computed: {
    canMarkAsPaid() {
      return (
        this.paymentStatusForm.payment_date &&
        this.paymentStatusForm.bankaccount &&
        this.paymentStatusForm.bankaccount._id
      );
    },
    paymentStatusRequestFiles() {
      return this.normalizePaymentRequestFileList(
        this.paymentStatusForm.payment_request_file
      );
    },
  },
  watch: {
    "paymentStatusForm.payment_date": {
      handler(newVal) {
        if (newVal && this.visible) {
          this.updateDateInput();
        }
        this.checkAndMarkAsPaid();
      },
    },
    "paymentStatusForm.bankaccount": {
      handler() {
        this.checkAndMarkAsPaid();
      },
      deep: true,
    },
  },
  methods: {
    formatNumber(number) {
      if (!number) return "0.00";
      return formatNumberSync(number, null, true);
    },
    getContactName(contact) {
      if (!contact) return "N/A";
      if (typeof contact === "object" && contact.name) return contact.name;
      return contact;
    },
    async loadPaymentFields() {
      try {
        const response = await wencesApi.get("/api/financial/payments/fields");
        if (response.data && response.data.success) {
          this.paymentFields = response.data.data;
          this.bankAccountField = this.paymentFields.find(
            (field) => field.key === "bankaccount"
          );
          if (this.bankAccountField) {
            this.bankAccountField.preload = true;
          }
          this.bankAccountToField =
            this.paymentFields.find((field) => field && field.key === "bank_account_to") ||
            null;
          if (this.bankAccountToField) {
            this.bankAccountToField.preload = true;
          }
        }
      } catch (error) {
        console.error("Error loading payment fields:", error);
        this.$toast?.error?.(
          "Error loading payment fields: " +
            (error.response?.data?.message || error.message)
        );
      }
    },
    async open(payment) {
      if (!payment || !payment._id) return;
      await this.loadPaymentFields();

      let p = payment;
      const getRefId = (val) => {
        if (!val) return "";
        if (typeof val === "string") return val;
        if (typeof val === "object" && val._id) return String(val._id);
        return "";
      };
      const hasDisplayLabel = (val) =>
        !!(
          val &&
          typeof val === "object" &&
          ((val.name && String(val.name).trim()) || (val.label && String(val.label).trim()))
        );
      try {
        const response = await wencesApi.get(
          `/api/gotrading/dynamic/payment/${payment._id}`
        );
        if (response.data && response.data.data) {
          const fetched = response.data.data || {};
          const incomingBank = payment.bankaccount;
          const fetchedBank = fetched.bankaccount;
          const incomingBankId = getRefId(incomingBank);
          const fetchedBankId = getRefId(fetchedBank);

          // Preserve populated bank account object when dynamic endpoint returns only id.
          if (
            incomingBank &&
            incomingBankId &&
            fetchedBankId &&
            incomingBankId === fetchedBankId &&
            !hasDisplayLabel(fetchedBank) &&
            hasDisplayLabel(incomingBank)
          ) {
            fetched.bankaccount = incomingBank;
          } else if (
            incomingBank &&
            incomingBankId &&
            !fetchedBankId &&
            hasDisplayLabel(incomingBank)
          ) {
            fetched.bankaccount = incomingBank;
          }

          const incomingBankTo = payment.bank_account_to;
          const fetchedBankTo = fetched.bank_account_to;
          const incomingBankToId = getRefId(incomingBankTo);
          const fetchedBankToId = getRefId(fetchedBankTo);
          if (
            incomingBankTo &&
            incomingBankToId &&
            fetchedBankToId &&
            incomingBankToId === fetchedBankToId &&
            !hasDisplayLabel(fetchedBankTo) &&
            hasDisplayLabel(incomingBankTo)
          ) {
            fetched.bank_account_to = incomingBankTo;
          } else if (
            incomingBankTo &&
            incomingBankToId &&
            !fetchedBankToId &&
            hasDisplayLabel(incomingBankTo)
          ) {
            fetched.bank_account_to = incomingBankTo;
          }

          p = fetched;
        }
      } catch (error) {
        console.error("Error loading payment details:", error);
      }

      this.selectedPayment = p;
      this.initializePaymentForm(p);
      this.paymentStatusFormErrors = {};
      this.serverValidationError = "";
      this.visible = true;

      this.$nextTick(() => {
        this.updateDateInput();
      });

      if (this.bankAccountField && this.bankAccountField.ref) {
        await this.loadReferenceOptions(this.bankAccountField.ref, "bankaccount");
      } else if (this.bankAccountToField && this.bankAccountToField.ref) {
        await this.loadReferenceOptions(this.bankAccountToField.ref, "bankaccount");
      }
      await this.loadExpenseTypeOptions();
      await this.loadReferenceOptions("shipping_expenses", "expense");
      await this.loadAppliedToData(p);

      // Snapshot AFTER appliedto load so Update Payment does not re-send lines.
      this.originalPaymentStatusForm = JSON.parse(
        JSON.stringify(this.paymentStatusForm)
      );
    },
    closeModal() {
      this.visible = false;
      this.selectedPayment = null;
      this.bankAccountToField = null;
      this.paymentStatusForm = {
        payment_date: "",
        bankaccount: "",
        bank_account_to: "",
        comment: "",
        paid: true,
        payment_request_file: [],
        appliedto: [],
      };
      this.paymentStatusFormErrors = {};
      this.serverValidationError = "";
      this.uploadingFile = false;
      this.uploadProgress = 0;
      this.referenceOptions = {};
      this.expenseTypeOptions = [];
      this.originalPaymentStatusForm = null;
      this.appliedToLoadIncomplete = false;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
      }
    },
    onBankAccountFieldChange(value) {
      this.paymentStatusForm.bankaccount = value;
      if (this.paymentStatusFormErrors.bankaccount) {
        delete this.paymentStatusFormErrors.bankaccount;
      }
    },
    onBankAccountToFieldChange(value) {
      this.paymentStatusForm.bank_account_to = value;
      if (this.paymentStatusFormErrors.bank_account_to) {
        delete this.paymentStatusFormErrors.bank_account_to;
      }
    },
    checkAndMarkAsPaid() {
      if (this.canMarkAsPaid && !this.paymentStatusForm.paid) {
        this.paymentStatusForm.paid = true;
      } else if (!this.canMarkAsPaid && this.paymentStatusForm.paid) {
        this.paymentStatusForm.paid = false;
      }
    },
    onPaidStatusChange(isPaid) {
      if (isPaid && !this.canMarkAsPaid) {
        this.paymentStatusForm.paid = false;
        return;
      }
      if (!isPaid) {
        this.paymentStatusForm.payment_date = "";
      } else if (!this.paymentStatusForm.payment_date) {
        this.paymentStatusForm.payment_date = new Date()
          .toISOString()
          .split("T")[0];
      }
      if (this.paymentStatusFormErrors.payment_date) {
        delete this.paymentStatusFormErrors.payment_date;
      }
    },
    initializePaymentForm(payment) {
      let formattedDate = "";
      if (payment.payment_date) {
        if (typeof payment.payment_date === "string") {
          const date = new Date(payment.payment_date);
          if (!isNaN(date.getTime())) {
            formattedDate = date.toISOString().split("T")[0];
          }
        } else if (payment.payment_date instanceof Date) {
          formattedDate = payment.payment_date.toISOString().split("T")[0];
        }
      }
      if (!formattedDate) {
        formattedDate = new Date().toISOString().split("T")[0];
      }

      let bankAccountObject = null;
      if (payment.bankaccount) {
        if (
          typeof payment.bankaccount === "object" &&
          payment.bankaccount._id
        ) {
          bankAccountObject = payment.bankaccount;
        } else if (typeof payment.bankaccount === "string") {
          bankAccountObject = {
            _id: payment.bankaccount,
            name: payment.bankaccount,
          };
        }
      }

      let bankAccountToObject = null;
      if (this.bankAccountToField && payment.bank_account_to) {
        if (
          typeof payment.bank_account_to === "object" &&
          payment.bank_account_to._id
        ) {
          bankAccountToObject = payment.bank_account_to;
        } else if (typeof payment.bank_account_to === "string") {
          bankAccountToObject = {
            _id: payment.bank_account_to,
            name: payment.bank_account_to,
          };
        }
      }

      this.paymentStatusForm = {
        payment_date: formattedDate,
        bankaccount: bankAccountObject,
        bank_account_to: bankAccountToObject,
        comment: payment.comment || "",
        paid: payment.paid !== undefined ? payment.paid : true,
        payment_request_file: this.normalizePaymentRequestFileList(
          payment.payment_request_file
        ),
        appliedto: [],
      };

      if (!this.paymentStatusForm.paid) {
        this.paymentStatusForm.payment_date = "";
      }
    },
    updateDateInput() {
      this.$nextTick(() => {
        const root = this.$el;
        if (!root) return;
        const dateInput = root.querySelector('input[type="date"]');
        if (dateInput && this.paymentStatusForm.payment_date) {
          dateInput.value = this.paymentStatusForm.payment_date;
        }
      });
    },
    getChangedFields() {
      if (!this.originalPaymentStatusForm) {
        return {};
      }
      const changedFields = {};
      const isEqual = (val1, val2) => {
        if (val1 === val2) return true;
        if (val1 == null || val2 == null) return val1 === val2;
        if (typeof val1 === "object" && typeof val2 === "object") {
          if (val1._id && val2._id) {
            return String(val1._id) === String(val2._id);
          }
          return JSON.stringify(val1) === JSON.stringify(val2);
        }
        return false;
      };

      Object.keys(this.paymentStatusForm).forEach((key) => {
        const currentValue = this.paymentStatusForm[key];
        const originalValue = this.originalPaymentStatusForm[key];
        if (key === "appliedto") {
          if (!appliedToRowsEqual(currentValue, originalValue)) {
            changedFields[key] = currentValue;
          }
          return;
        }
        if (key === "bankaccount" || key === "bank_account_to") {
          if (paymentRefId(currentValue) !== paymentRefId(originalValue)) {
            changedFields[key] = currentValue;
          }
          return;
        }
        if (!isEqual(currentValue, originalValue)) {
          changedFields[key] = currentValue;
        }
      });
      return changedFields;
    },
    async loadExpenseTypeOptions() {
      try {
        const response = await wencesApi.get("/api/gotrading/lists");
        this.expenseTypeOptions = typeofpaymentOptionsFromLists(response.data);
      } catch (error) {
        console.error("Error loading expense type options:", error);
        this.expenseTypeOptions = [];
      }
    },
    async loadReferenceOptions(modelName, fieldKey) {
      try {
        const response = await wencesApi.get(
          `/api/gotrading/dynamic/${modelName}`
        );
        const items = extractDynamicListItems(response.data);
        const options =
          fieldKey === "expense"
            ? applyMarkPaidExpenseLabels(items, {
                typeOptions: this.expenseTypeOptions,
              })
            : items;
        if (options.length) {
          this.$set(this.referenceOptions, fieldKey, options);
        }
      } catch (error) {
        console.error("Error loading reference options:", error);
      }
    },
    async loadAppliedToData(payment) {
      const { rows, complete } = await loadPaymentAppliedToRows(
        wencesApi,
        payment
      );
      this.appliedToLoadIncomplete = !complete;

      if (rows.length === 0) return;

      this.paymentStatusForm.appliedto = await Promise.all(
        rows.map(async (applied) => {
          const expenseId = paymentRefId(applied.expense);
          const expenseOption = (this.referenceOptions.expense || []).find(
            (exp) =>
              exp._id === expenseId || exp._id?.toString() === String(expenseId)
          );
          const expenseValue = await resolveMarkPaidExpenseOption(
            wencesApi,
            expenseOption || applied.expense,
            {
              typeOptions: this.expenseTypeOptions,
              bankCommission: applied.bank_commission || 0,
            }
          );
          return {
            _id: applied._id,
            expense: expenseValue || null,
            amount: applied.amount || 0,
            bank_commission: applied.bank_commission || 0,
          };
        })
      );
    },
    addAppliedTo() {
      this.paymentStatusForm.appliedto.push({
        expense: null,
        amount: 0,
        bank_commission: 0,
      });
    },
    removeAppliedTo(index) {
      this.paymentStatusForm.appliedto.splice(index, 1);
    },
    async onExpenseChange(index, value) {
      if (!this.paymentStatusForm.appliedto[index]) return;
      const row = this.paymentStatusForm.appliedto[index];
      const expenseValue = await resolveMarkPaidExpenseOption(
        wencesApi,
        value,
        {
          typeOptions: this.expenseTypeOptions,
          bankCommission: row.bank_commission || 0,
        }
      );
      this.$set(this.paymentStatusForm.appliedto[index], "expense", expenseValue);
    },
    getTotalAppliedAmount() {
      if (
        !this.paymentStatusForm.appliedto ||
        !Array.isArray(this.paymentStatusForm.appliedto)
      ) {
        return 0;
      }
      return this.paymentStatusForm.appliedto.reduce((total, applied) => {
        return total + (parseFloat(applied.amount) || 0);
      }, 0);
    },
    hasAppliedToError() {
      const totalApplied = this.getTotalAppliedAmount();
      const paymentAmount = this.selectedPayment?.amount || 0;
      return exceedsMoneyLimit(totalApplied, paymentAmount);
    },
    getTotalAppliedClass() {
      if (this.hasAppliedToError()) {
        return "text-danger fw-bold";
      }
      return "text-success fw-bold";
    },
    async handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        this.$toast?.error?.("File size must be less than 10MB");
        return;
      }
      const allowedTypes = [
        "image/jpeg",
        "image/png",
        "image/gif",
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];
      if (!allowedTypes.includes(file.type)) {
        this.$toast?.error?.(
          "File type not supported. Please upload images, PDF, or Word documents."
        );
        return;
      }
      try {
        this.uploadingFile = true;
        this.uploadProgress = 0;
        const formData = new FormData();
        formData.append("image", file);
        const response = await wencesApi.post("/api/upload/upload", formData, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (progressEvent) => {
            this.uploadProgress = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
          },
        });
        if (response.data.fileKey) {
          this.paymentStatusForm.payment_request_file = [response.data.fileKey];
          this.$toast?.success?.("File uploaded successfully");
        } else {
          throw new Error(response.data.message || "Failed to upload file");
        }
      } catch (error) {
        console.error("Error uploading file:", error);
        this.$toast?.error?.(
          "Error uploading file: " +
            (error.response?.data?.message || error.message)
        );
      } finally {
        this.uploadingFile = false;
        this.uploadProgress = 0;
      }
    },
    removeFile() {
      this.paymentStatusForm.payment_request_file = [];
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
      }
    },
    getFileName(file) {
      const ref = typeof file === "string" ? file : file?.url;
      return (
        getFileuploadDisplayName(ref || "") ||
        file?.name ||
        this.$t("Unknown file")
      );
    },
    async viewFile(file) {
      const ref = typeof file === "string" ? file : file?.url;
      if (!ref) return;
      const result = await openFileupload(ref, { t: (k) => this.$t(k) });
      if (!result.ok) {
        showFileuploadAccessError(this, result.errorMessage, {
          title: this.$t("Error accessing file."),
          fileRef: ref,
        });
      }
    },
    async updatePaymentStatus() {
      if (this.updatingPaymentStatus) return;
      try {
        this.paymentStatusFormErrors = {};
        let hasErrors = false;

        if (!this.paymentStatusForm.payment_date) {
          Swal.fire({
            icon: "error",
            title: this.$tn("Validation Error"),
            text: this.$tn("Payment date is required"),
            confirmButtonText: this.$tn("OK"),
          });
          return;
        }

        if (!this.paymentStatusForm.bankaccount) {
          this.paymentStatusFormErrors.bankaccount = this.$t(
            "Bank account is required"
          );
          hasErrors = true;
        }

        if (this.bankAccountToField) {
          const fromId = paymentRefId(this.paymentStatusForm.bankaccount);
          const toId = paymentRefId(this.paymentStatusForm.bank_account_to);
          if (fromId && toId && fromId === toId) {
            const msg = this.$t(
              "Bank account (from) and Bank account (to) cannot be the same"
            );
            this.paymentStatusFormErrors.bank_account_to = msg;
            hasErrors = true;
            await Swal.fire({
              icon: "error",
              title: this.$tn("Validation Error"),
              text: msg,
              confirmButtonText: this.$tn("OK"),
            });
            return;
          }
        }

        const totalApplied = this.getTotalAppliedAmount();
        const paymentAmount = this.selectedPayment?.amount || 0;
        if (exceedsMoneyLimit(totalApplied, paymentAmount)) {
          Swal.fire({
            icon: "error",
            title: this.$tn("Validation Error"),
            text: this.$tn(
              "Total applied amount ({total}) cannot exceed payment amount ({amount})",
              {
                total: this.formatNumber(totalApplied),
                amount: this.formatNumber(paymentAmount),
              }
            ),
            confirmButtonText: this.$tn("OK"),
          });
          return;
        }

        if (hasErrors) return;

        this.serverValidationError = "";
        this.updatingPaymentStatus = true;

        const changedFields = this.getChangedFields();

        if (changedFields.appliedto && this.appliedToLoadIncomplete) {
          this.updatingPaymentStatus = false;
          Swal.fire({
            icon: "error",
            title: this.$tn("Validation Error"),
            text: this.$tn("paymentAppliedToIncompleteLoad"),
            confirmButtonText: this.$tn("OK"),
          });
          return;
        }

        if (Object.keys(changedFields).length === 0) {
          this.updatingPaymentStatus = false;
          this.closeModal();
          return;
        }

        const updateData = buildPaymentStatusUpdatePayload(changedFields);
        const paymentId = this.selectedPayment._id;

        const finishSuccess = () => {
          Swal.fire({
            icon: "success",
            title: "Éxito",
            text: "Payment updated successfully",
            timer: 3000,
            showConfirmButton: false,
          });
          this.closeModal();
          this.$emit("saved");
        };

        try {
          const response = await wencesApi.put(
            `/api/gotrading/dynamic/payment/${paymentId}`,
            updateData,
            { timeout: 120000 }
          );

          if (response.data) {
            finishSuccess();
          } else {
            throw new Error("Failed to update payment");
          }
        } catch (putError) {
          if (isPaymentUpdateTransportError(putError)) {
            const persisted = await verifyPaymentStatusPersisted(
              wencesApi,
              paymentId,
              {
                paid: this.paymentStatusForm.paid,
                payment_date: this.paymentStatusForm.payment_date,
                bankaccount: this.paymentStatusForm.bankaccount,
              }
            );
            if (persisted) {
              finishSuccess();
              return;
            }
          }
          throw putError;
        }
      } catch (error) {
        console.error("Error updating payment status:", error);
        let errorMessage = this.$t("Error updating payment");
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.response?.data?.error) {
          errorMessage = error.response.data.error;
        } else if (isPaymentUpdateTransportError(error)) {
          errorMessage = this.$t("Error updating payment");
        } else if (error.message) {
          errorMessage = error.message;
        }
        this.serverValidationError = errorMessage;
        Swal.fire({
          icon: "error",
          title: this.$tn("Validation Error"),
          text: errorMessage,
          confirmButtonText: this.$tn("Understood"),
        });
      } finally {
        this.updatingPaymentStatus = false;
      }
    },
  },
};
</script>
